import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OcrFileProgress, OcrProgressService, OcrStage } from '../../../services/ocr/ocr-progress.service';

interface StageInfo {
  title: string;
  hint: string;
  icon: string;
  step: number;
  percent: number;
  tone: 'work' | 'wait' | 'ok' | 'warn' | 'bad';
  action?: { label: string; route: string };
}

@Component({
  selector: 'app-ocr-progress-panel',
  templateUrl: './ocr-progress-panel.component.html',
  styleUrls: ['./ocr-progress-panel.component.css'],
  standalone: false
})
export class OcrProgressPanelComponent implements OnInit {
  readonly steps = ['Recibida', 'En cola', 'Leyendo', 'Gasto listo'];

  items$: Observable<OcrFileProgress[]>;
  activeCount$: Observable<number>;
  hasFinished$: Observable<boolean>;
  headline$: Observable<{ title: string; subtitle: string; busy: boolean }>;
  collapsed = false;

  private readonly stages: { [stage: string]: StageInfo } = {
    pending: { title: 'Factura recibida', hint: 'La hemos recibido y la vamos a preparar.', icon: 'inbox', step: 0, percent: 12, tone: 'work' },
    queued: { title: 'En cola', hint: 'Enseguida le toca, no hace falta que hagas nada.', icon: 'hourglass_empty', step: 1, percent: 32, tone: 'wait' },
    waiting: { title: 'Esperando su turno', hint: 'Hay otra factura del mismo proveedor delante. Así evitamos duplicar su ficha.', icon: 'hourglass_empty', step: 1, percent: 40, tone: 'wait' },
    extracting: { title: 'Leyendo la factura', hint: 'Estamos sacando proveedor, fecha, importes e impuestos.', icon: 'find_in_page', step: 2, percent: 62, tone: 'work' },
    creating: { title: 'Preparando el gasto', hint: 'Casi listo, estamos creando el gasto.', icon: 'edit_note', step: 3, percent: 88, tone: 'work' },
    done: { title: 'Gasto creado', hint: 'Ya lo tienes en tu lista de gastos.', icon: 'check_circle', step: 4, percent: 100, tone: 'ok', action: { label: 'Ver gastos', route: '/expense/list' } },
    duplicate: { title: 'Ya estaba registrada', hint: 'Esta factura ya existe en tus gastos, no hemos creado otra.', icon: 'content_copy', step: 4, percent: 100, tone: 'warn', action: { label: 'Ver gastos', route: '/expense/list' } },
    attention: { title: 'Hay que revisarla', hint: 'Falta algún dato para terminar. Échale un vistazo.', icon: 'rate_review', step: 4, percent: 100, tone: 'warn', action: { label: 'Revisar', route: '/expense/ia' } },
    error: { title: 'No hemos podido leerla', hint: 'No pasa nada: puedes crear el gasto a mano.', icon: 'error_outline', step: 4, percent: 100, tone: 'bad', action: { label: 'Crear gasto', route: '/expense/create' } }
  };

  constructor(private ocrProgress: OcrProgressService, private router: Router) { }

  ngOnInit(): void {
    this.items$ = this.ocrProgress.items$;
    this.activeCount$ = this.items$.pipe(map(items => items.filter(item => this.isActive(item.stage)).length));
    this.hasFinished$ = this.items$.pipe(map(items => items.some(item => !this.isActive(item.stage))));

    this.headline$ = this.items$.pipe(map(items => {
      const active = items.filter(item => this.isActive(item.stage)).length;
      const problems = items.filter(item => ['error', 'attention', 'duplicate'].indexOf(item.stage) !== -1).length;

      if (active > 0) {
        return {
          busy: true,
          title: active === 1 ? 'Estamos leyendo tu factura' : `Estamos leyendo ${active} facturas`,
          subtitle: 'Puedes seguir trabajando, aquí verás cómo va.'
        };
      }

      if (problems > 0) {
        return {
          busy: false,
          title: problems === 1 ? 'Una factura necesita tu atención' : `${problems} facturas necesitan tu atención`,
          subtitle: 'El resto ya está lista.'
        };
      }

      return { busy: false, title: 'Todo listo', subtitle: 'Tus facturas ya se han convertido en gastos.' };
    }));
  }

  info(stage: OcrStage): StageInfo {
    return this.stages[stage] || this.stages['pending'];
  }

  isActive(stage: OcrStage): boolean {
    return !OcrProgressService.isFinal(stage);
  }

  stepState(item: OcrFileProgress, index: number): 'done' | 'current' | 'todo' {
    const step = this.info(item.stage).step;

    if (index < step) return 'done';
    if (index === step && this.isActive(item.stage)) return 'current';
    return 'todo';
  }

  go(item: OcrFileProgress): void {
    const action = this.info(item.stage).action;

    if (action) {
      this.router.navigate([action.route]);
      this.dismiss(item);
    }
  }

  dismiss(item: OcrFileProgress): void {
    this.ocrProgress.dismiss(item.fileUid);
  }

  clearFinished(): void {
    this.ocrProgress.clearFinished();
  }

  trackByUid(_: number, item: OcrFileProgress): string {
    return item.fileUid;
  }
}
